/**
 * Polish business identifiers, as a venue's owner types them.
 *
 * These are validated on the client as a courtesy, not as a guarantee: the
 * checksums below are cheap, deterministic and catch the transposed digit that a
 * format check waves through. A wrong NIP is worse than a blank one — it ends up
 * on an invoice and in a JPK filing, and Viva rejects it at the point where the
 * venue is trying to get paid. Whatever endpoint eventually stores these has to
 * repeat the check, because nothing that runs in a browser is a control.
 */

/** Owners type NIPs with dashes, spaces or neither; all three mean one number. */
export const normalizeDigits = (value: string): string =>
  value.replace(/[\s-]/g, '')

/**
 * Turns what somebody typed into `12-345`, for use on blur.
 *
 * Rejecting `12345` would be pedantry: it is unambiguous, it is how the digits
 * are spoken, and the dash is ours to add.
 */
export const normalizePostalCode = (value: string): string => {
  const digits = normalizeDigits(value)
  if (digits.length !== 5 || !/^\d{5}$/.test(digits)) return value.trim()
  return `${digits.slice(0, 2)}-${digits.slice(2)}`
}

const checksum = (digits: string, weights: number[]): number =>
  weights.reduce((sum, weight, index) => sum + weight * Number(digits[index]), 0)

const NIP_WEIGHTS = [6, 5, 7, 2, 3, 4, 5, 6, 7]

/**
 * Ten digits, the last of which is the weighted sum of the other nine mod 11.
 *
 * A remainder of 10 cannot be written in one digit, so such a number was never
 * issued and is invalid rather than "check digit 0" — getting that wrong accepts
 * a whole class of transpositions.
 */
export const isValidNip = (value: string): boolean => {
  const digits = normalizeDigits(value)
  if (!/^\d{10}$/.test(digits)) return false

  const remainder = checksum(digits, NIP_WEIGHTS) % 11
  if (remainder === 10) return false
  return remainder === Number(digits[9])
}

const REGON_9_WEIGHTS = [8, 9, 2, 3, 4, 5, 6, 7]
const REGON_14_WEIGHTS = [2, 4, 8, 5, 0, 9, 7, 3, 6, 1, 2, 4, 8]

/**
 * Nine digits for a business, fourteen for a local unit of one.
 *
 * Both are the same scheme with different weights, and both treat a remainder of
 * 10 as a check digit of 0 — the opposite of NIP above, which is why they cannot
 * share one function.
 */
export const isValidRegon = (value: string): boolean => {
  const digits = normalizeDigits(value)
  if (!/^\d{9}$/.test(digits) && !/^\d{14}$/.test(digits)) return false

  const weights = digits.length === 9 ? REGON_9_WEIGHTS : REGON_14_WEIGHTS
  const remainder = checksum(digits, weights) % 11
  const checkDigit = remainder === 10 ? 0 : remainder
  return checkDigit === Number(digits[digits.length - 1])
}

/**
 * Deliberately loose: `+`, digits, spaces and dashes, 9 to 15 digits.
 *
 * Not a Polish matcher. A venue may publish a mobile, a landline, a VoIP number
 * or a foreign one, and a strict pattern rejects real numbers for no gain.
 * Normalising to E.164 belongs on the server, which already declares
 * `phoneNumberRegion: 'PL'`.
 */
export const isPlausiblePhone = (value: string): boolean => {
  if (!/^\+?[\d\s-]+$/.test(value)) return false
  const digits = value.replace(/\D/g, '')
  return digits.length >= 9 && digits.length <= 15
}
